import { StoryblokMultilink } from '@definitions/types/storyblokBaseTypes'
import { AppTheme } from 'styled-components'

// Hardcoded URLs - to be moved to environment variables in the future
const URL_SUNPOINT_MAIN = 'https://www.sunpoint.de/'
const URL_WELLMAXX_MAIN = 'https://www.wellmaxx-bodyforming.com/'

/**
 * Converts a slug string by replacing German umlaut encodings and capitalizing the first letter
 * @param slug - The slug string to convert (e.g., "muenchen" -> "München")
 * @returns The deslugified string with first letter capitalized
 */
const deslugify = (slug: string): string => {
  if (!slug || slug.length === 0) {
    return slug
  }

  const deslugified = slug
    .replace(/ae/g, 'ä')
    .replace(/ue/g, 'ü')
    .replace(/oe/g, 'ö')
  return deslugified.charAt(0).toUpperCase() + deslugified.slice(1)
}

/**
 * Converts a URL string into an array of breadcrumb objects
 * @param url - The URL to convert (e.g., "/sunpoint/braeune/solarium")
 * @returns Array of breadcrumb objects with text and link properties
 */
const urlToBreadcrumbs = (url: string): { text: string; link: string }[] => {
  if (!url || url.trim().length === 0) {
    return []
  }

  // Remove query params and fragments, then split by '/'
  const cleanUrl = url.split('?')[0].split('#')[0]
  const urlParts = cleanUrl.split('/').filter((p) => p !== '')
  const breadcrumbs: { text: string; link: string }[] = []
  const currentUrlParts: string[] = []

  for (let [index, urlPart] of urlParts.entries()) {
    currentUrlParts.push(urlPart)

    breadcrumbs.push({
      text: index === 0 ? 'Home' : deslugify(urlPart),
      link: `/${currentUrlParts.join('/')}`,
    })
  }

  return breadcrumbs
}

/**
 * Builds a URL by ensuring proper slash handling between base URL and path
 * @param baseUrl - The base URL (e.g., "https://www.sunpoint.de/")
 * @param path - The path to append (e.g., "sunpoint/studio")
 * @returns The combined URL with proper slashes
 */
const buildUrl = (baseUrl: string, path: string): string => {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  return base + cleanPath
}

/**
 * Handles cross-brand story links (Sunpoint <-> Wellmaxx)
 * @param cachedUrl - The cached URL from Storyblok
 * @param themeKey - The current theme key ('sunpoint' or 'wellmaxx')
 * @returns The resolved URL or undefined if no match
 */
const handleCrossBrandStoryLink = (
  cachedUrl: string | undefined,
  themeKey: string | undefined
): string | undefined => {
  if (!cachedUrl || !themeKey) {
    return undefined
  }

  // Sunpoint -> Wellmaxx link
  if (cachedUrl.includes('wellmaxx/')) {
    if (themeKey === 'sunpoint') {
      return buildUrl(URL_WELLMAXX_MAIN, cachedUrl)
    }
    if (themeKey === 'wellmaxx') {
      return `/${cachedUrl}`
    }
  }

  // Wellmaxx -> Sunpoint link
  if (cachedUrl.includes('sunpoint/')) {
    if (themeKey === 'sunpoint') {
      return `/${cachedUrl}`
    }
    if (themeKey === 'wellmaxx') {
      return buildUrl(URL_SUNPOINT_MAIN, cachedUrl)
    }
  }

  return undefined
}



/**
 * Extracts a URL from a Storyblok Multilink object based on link type and theme
 * @param link - The Storyblok Multilink object (excluding email and asset types)
 * @param theme - The current app theme
 * @returns The resolved URL string or undefined if no valid URL can be determined
 */
const getUrlFromLink = (
  link: StoryblokMultilink | undefined,
  theme: AppTheme
): string | undefined => {
  if (link === undefined) {
    return undefined
  }

  // Handle direct URL links
  if (link.linktype === 'url' && link.url !== undefined) {
    return link.url
  }

  // Handle story links
  if (link.linktype === 'story' && link.cached_url) {
    // Only try cross-brand resolution if theme and theme.key are available
    // This prevents hydration mismatches when theme is undefined during SSR
    if (theme?.key) {
      const crossBrandUrl = handleCrossBrandStoryLink(link.cached_url, theme.key)
      if (crossBrandUrl !== undefined) {
        return crossBrandUrl
      }
    }

    // Handle same-brand story links (fallback to relative path)
    return `/${link.cached_url}`
  }

  // Fallback for unknown link types or missing data
  return undefined
}

export { urlToBreadcrumbs, deslugify, getUrlFromLink }
